import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {
  Divider,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Map } from "ol";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useThirdPartyApi,
  WindDirectionMeasurement,
  WindGustMeasurement,
  WindSpeedMeasurement,
} from "~/api/GeneratedApi";
import { TabPanel } from "~/components/GenericTab";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import { useDateFormatter } from "~/utils/hooks/useDateFormatter";
import { WindMapItem } from "../types/mapItems";
import { GenericPopup } from "./GenericPopup";

const useStyles = makeStyles(() => ({
  tab: {
    margin: 0,
  },
}));

type Props = {
  map: Map;
} & (ClickProps | HoverProps);

type ClickProps = {
  clickData: WindMapItem;
  hoverData?: WindMapItem;
};

type HoverProps = {
  hoverData: WindMapItem;
  clickData?: WindMapItem;
};

type HistoryMeasurement = {
  windSpeed: WindSpeedMeasurement;
  windGust?: WindGustMeasurement;
  windDirection?: WindDirectionMeasurement;
};

export const WindPopup = ({
  hoverData,
  clickData,
  map,
}: Props): JSX.Element | null => {
  const classes = useStyles();
  const [tab, setTab] = useState(0);
  const [historyMeasurements, setHistoryMeasurements] =
    useState<HistoryMeasurement[]>();
  const { t } = useTranslation("weather");
  const dateFormatter = useDateFormatter();
  const { getWeatherHistory } = useThirdPartyApi();

  const data = clickData ?? hoverData;

  const { data: historyData, isLoading: historyLoading } = useCustomQuery(
    { key: "weather-history", id: clickData?.id },
    () => getWeatherHistory({ station: clickData?.id }),
    {
      enabled: !!clickData?.id,
    },
  );

  // Map history response to state
  useEffect(() => {
    if (!historyData || !clickData?.id) return;
    const stationHistory = historyData?.find((h) => h.id === clickData.id);
    if (!stationHistory || !stationHistory.windSpeedMeasurements) return;
    const history: HistoryMeasurement[] =
      stationHistory.windSpeedMeasurements.map((s) => {
        // Matching gust and direction
        const gust = stationHistory.windGustMeasurements?.find(
          (g) => g.timestamp.valueOf() === s.timestamp.valueOf(),
        );
        const dir = stationHistory.windDirectionMeasurements?.find(
          (d) => d.timestamp.valueOf() === s.timestamp.valueOf(),
        );
        return {
          windSpeed: s,
          windGust: gust,
          windDirection: dir,
        };
      });
    setHistoryMeasurements(history);
  }, [historyData, clickData?.id]);

  if (!data) return null;

  const {
    stationName,
    latestSpeedValue,
    region,
    windSpeedMeasurements,
    windGustMeasurements,
    windDirectionMeasurements,
  } = data;

  const handleChange = (
    _event: React.ChangeEvent<unknown>,
    newValue: number,
  ) => {
    setTab(newValue);
  };

  return (
    <GenericPopup
      map={map}
      clicked={!!clickData}
      avatarText={
        latestSpeedValue
          ? `${latestSpeedValue.value} ${latestSpeedValue.unit}`
          : "-"
      }
      title={`${t("wind-speed")} - ${stationName ?? ""}`}
      subheader={region ?? ""}
      content={
        <>
          <Tabs
            value={tab}
            onChange={handleChange}
            TabIndicatorProps={{ className: classes.tab }}
            variant="fullWidth"
            indicatorColor="secondary"
          >
            <Tab label={t("latest-measurements")} className={classes.tab} />
            <Tab
              label={t("measurement-history")}
              disabled={historyLoading}
              className={classes.tab}
            />
          </Tabs>
          <TabPanel index={0} value={tab}>
            {windSpeedMeasurements?.length ? (
              <>
                <Typography align="center" variant="body2" noWrap>
                  {`${t("wind-speed")} | ${t("wind-gust")} | ${t(
                    "wind-direction",
                  )}`}
                </Typography>
                <Divider />
                <List>
                  {windSpeedMeasurements?.map((m, index) => {
                    // Matching gust and direction
                    const gust = windGustMeasurements?.find(
                      (g) => g.timestamp.valueOf() === m.timestamp.valueOf(),
                    );
                    const dir = windDirectionMeasurements?.find(
                      (d) => d.timestamp.valueOf() === m.timestamp.valueOf(),
                    );
                    return (
                      <ListItem
                        dense
                        alignItems="center"
                        style={{ textAlign: "center" }}
                        key={m.timestamp.valueOf()}
                        divider={index < windSpeedMeasurements.length - 1}
                      >
                        <ListItemText
                          primary={
                            <>
                              {`${m.value} ${m.unit}${
                                gust ? ` | ${gust.value} ${gust.unit}` : ""
                              }`}
                              {dir && (
                                <>
                                  {" | "}
                                  <ArrowDownwardIcon
                                    style={{
                                      transform: `rotate(${dir.value}deg)`,
                                      fontSize: "inherit",
                                    }}
                                  />
                                </>
                              )}
                            </>
                          }
                          secondary={dateFormatter({ date: m.timestamp })}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </>
            ) : (
              <Typography align="center" variant="body2">
                {t("no-measurements")}
              </Typography>
            )}
          </TabPanel>
          <TabPanel index={1} value={tab}>
            <>
              {historyMeasurements?.length ? (
                <>
                  <Typography align="center" variant="body2" noWrap>
                    {`${t("wind-speed")} | ${t("wind-gust")} | ${t(
                      "wind-direction",
                    )}`}
                  </Typography>
                  <Divider />
                  <List>
                    {historyMeasurements?.map((m, index) => {
                      return (
                        <ListItem
                          dense
                          alignItems="center"
                          style={{ textAlign: "center" }}
                          key={m.windSpeed.timestamp.valueOf()}
                          divider={index < historyMeasurements.length - 1}
                        >
                          <ListItemText
                            primary={
                              <>
                                {`${m.windSpeed.value} ${m.windSpeed.unit}${
                                  m.windGust
                                    ? ` | ${m.windGust.value} ${m.windGust.unit}`
                                    : ""
                                }`}
                                {m.windDirection && (
                                  <>
                                    {" | "}
                                    <ArrowDownwardIcon
                                      style={{
                                        transform: `rotate(${m.windDirection.value}deg)`,
                                        fontSize: "inherit",
                                      }}
                                    />
                                  </>
                                )}
                              </>
                            }
                            secondary={dateFormatter({
                              date: m.windSpeed.timestamp,
                            })}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </>
              ) : (
                <Typography align="center" variant="body2">
                  {t("no-measurements")}
                </Typography>
              )}
            </>
          </TabPanel>
        </>
      }
    />
  );
};
